import { Badge, Button, Card, CardContent, Loader } from "@devhop/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { EditIcon, MapPin, UserPlus } from "lucide-react";
import { useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import {
  canUseMerchantPermission,
  type ShopBranchAdmin,
} from "@/modules/merchant/presentation/api/client";
import { shopBreadcrumbs } from "@/modules/merchant/presentation/lib/shop-breadcrumb";
import { useMerchantShops } from "@/modules/merchant/presentation/store/use-merchant-shops";
import { RequireMerchantPermission } from "@/modules/merchant/presentation/ui/RequireMerchantPermission";
import { ShopAssignStaffToBranchModal } from "@/modules/merchant/presentation/ui/ShopAssignStaffToBranchModal";
import { ShopBranchFormModal } from "@/modules/merchant/presentation/ui/ShopBranchFormModal";
import { MerchantPermissions } from "@/modules/roles/domain/contracts/merchant-permissions";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { RowActions } from "@/shared/ui/RowActions";
import { shopBranchesKeys, useShopBranchesListQuery } from "../api/queries";

export function ShopBranchesPage() {
  const params = useParams({ strict: false });
  const shopId = params?.shopId as string | undefined;
  const qc = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [editBranch, setEditBranch] = useState<ShopBranchAdmin | null>(null);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignInitialBranch, setAssignInitialBranch] =
    useState<ShopBranchAdmin | null>(null);

  const list = useShopBranchesListQuery(shopId);
  const { currentPermissions, currentShop } = useMerchantShops(shopId);
  const verifiedLevel = currentShop?.verifiedLevel ?? null;

  const canUpdate = canUseMerchantPermission(
    currentPermissions,
    MerchantPermissions.shop_staff.update,
    verifiedLevel,
  );

  const items = list.data ?? [];

  const onSuccess = () => {
    if (shopId)
      qc.invalidateQueries({ queryKey: shopBranchesKeys.all(shopId) });
  };

  return (
    <RequireMerchantPermission permission={MerchantPermissions.shop_staff.read}>
      <Header>
        <Breadcrumb items={shopBreadcrumbs.branches(shopId)} />
      </Header>

      <Main>
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="font-semibold text-foreground text-lg">ສາຂາ</h1>
            <p className="text-muted-foreground text-sm">
              ມອບໝາຍພະນັກງານເຂົ້າສາຂາແບບຕິກລາຍການ (ຕ້ອງມີສິດແກ້ໄຂພະນັກງານ)
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {canUpdate && shopId ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setAssignInitialBranch(null);
                  setAssignOpen(true);
                }}
              >
                <UserPlus className="size-4" />
                ມອບໝາຍພະນັກງານ
              </Button>
            ) : null}
            {canUpdate ? (
              <Button
                type="button"
                onClick={() => {
                  setEditBranch(null);
                  setModalOpen(true);
                }}
              >
                ເພີ່ມສາຂາ
              </Button>
            ) : null}
          </div>
        </div>

        {list.isLoading ? (
          <div className="flex min-h-[200px] items-center justify-center rounded-xl border bg-card p-8">
            <Loader />
          </div>
        ) : items.length === 0 ? (
          <div className="flex min-h-[200px] flex-col items-center justify-center gap-3 rounded-xl border border-dashed bg-muted/20 p-8 text-center">
            <MapPin className="size-10 text-muted-foreground/60" />
            <p className="text-muted-foreground text-sm">
              ຍັງບໍ່ມີສາຂາ. ເພີ່ມສາຂາກ່ອນເພື່ອໃຊ້ສາງ ແລະ POS ຕາມສາຂາ.
            </p>
            <div className="flex flex-wrap justify-center gap-2">
              {canUpdate && shopId ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setAssignInitialBranch(null);
                    setAssignOpen(true);
                  }}
                >
                  <UserPlus className="size-4" />
                  ມອບໝາຍພະນັກງານ
                </Button>
              ) : null}
              {canUpdate ? (
                <Button
                  type="button"
                  onClick={() => {
                    setEditBranch(null);
                    setModalOpen(true);
                  }}
                >
                  ເພີ່ມສາຂາທຳອິດ
                </Button>
              ) : null}
            </div>
          </div>
        ) : (
          <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((b) => (
              <li key={b.id}>
                <Card className="h-full">
                  <CardContent className="flex h-full flex-col gap-2">
                    <div className="flex min-w-0 items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-semibold text-foreground">
                          {b.name}
                        </p>
                        {b.phone ? (
                          <p className="text-muted-foreground text-sm">
                            {b.phone}
                          </p>
                        ) : null}
                        {b.address ? (
                          <p className="line-clamp-2 text-muted-foreground text-sm">
                            {b.address}
                          </p>
                        ) : null}
                      </div>
                      {!b.isActive ? (
                        <Badge variant="secondary">ປິດໃຊ້ງານ</Badge>
                      ) : null}
                    </div>
                    {canUpdate && (
                      <div className="mt-auto border-t pt-3">
                        <RowActions
                          actions={[
                            {
                              label: "ມອບໝາຍພະນັກງານ",
                              icon: <UserPlus className="size-4" />,
                              onClick: () => {
                                setAssignInitialBranch(b);
                                setAssignOpen(true);
                              },
                            },
                            {
                              label: "ແກ້ໄຂ",
                              icon: <EditIcon className="size-4" />,
                              onClick: () => {
                                setEditBranch(b);
                                setModalOpen(true);
                              },
                            },
                          ]}
                          maxInline={2}
                        />
                      </div>
                    )}
                  </CardContent>
                </Card>
              </li>
            ))}
          </ul>
        )}

        {shopId ? (
          <>
            <ShopAssignStaffToBranchModal
              open={assignOpen}
              onOpenChange={(open) => {
                setAssignOpen(open);
                if (!open) setAssignInitialBranch(null);
              }}
              shopId={shopId}
              branches={items}
              initialBranch={assignInitialBranch}
              canUpdateStaff={canUpdate}
              onSuccess={onSuccess}
            />
            <ShopBranchFormModal
              open={modalOpen}
              onOpenChange={(open) => {
                setModalOpen(open);
                if (!open) setEditBranch(null);
              }}
              shopId={shopId}
              mode={editBranch ? "edit" : "create"}
              branch={editBranch ?? undefined}
              onSuccess={onSuccess}
            />
          </>
        ) : null}
      </Main>
    </RequireMerchantPermission>
  );
}
